import {Component, computed, inject, OnInit} from '@angular/core';
import {SidebarNavigationButton} from './sidebar-navigation-button/sidebar-navigation-button';
import {CompanyRouteContextService} from '@services/company-route-context.service';
import {UserStateService} from '@services/frontend/user-state.service';
import {Role} from '@models/user.interface';
import {ExecutiveSubscriptionState} from '@services/executive-subscription.state';
import {ExpertWalletAccessState} from '@services/expert-wallet-access.state';

export interface INavigationItem {
  name: string;
  icon: string;
  link: string[];
}

export interface INavigationGroup {
  label: string;
  items: INavigationItem[];
}

@Component({
  selector: 'app-sidebar-navigation',
  imports: [SidebarNavigationButton],
  templateUrl: './sidebar-navigation.html',
})
export class SidebarNavigation implements OnInit {
  private readonly routeContext = inject(CompanyRouteContextService);
  private readonly userStateService = inject(UserStateService);
  private readonly executiveSubscriptionState = inject(ExecutiveSubscriptionState);
  private readonly expertWalletAccessState = inject(ExpertWalletAccessState);

  ngOnInit(): void {
    this.executiveSubscriptionState.ensureLoaded();
    if (this.userStateService.getUser()()?.role === Role.ACCOUNTANT) {
      this.expertWalletAccessState.ensureLoaded();
    }
  }

  navGroups = computed<INavigationGroup[]>(() => {
    const role = this.userStateService.getUser()()?.role;
    if (role === Role.ACCOUNTANT) {
      const items: INavigationItem[] = [
        {name: 'Mes clients', icon: 'groups', link: ['/dashboard', 'expert', 'clients']},
      ];
      if (this.userStateService.getUser()()?.admin) {
        items.push({name: 'Suivi fiscal', icon: 'receipt_long', link: ['/dashboard', 'expert', 'suivi-fiscal']});
      }
      if (this.expertWalletAccessState.hasAccess()) {
        items.push({name: 'QR Wallet', icon: 'qr_code_2', link: ['/dashboard', 'expert', 'wallet-qr']});
      }
      items.push({name: 'Paramètres', icon: 'settings', link: ['/dashboard', 'expert', 'parametres']});
      return [{label: 'Expert', items}];
    }

    const siret = this.routeContext.currentSiret();
    if (!siret) return [];

    const base = this.routeContext.companyBaseSegments(siret);
    const isFreeExecutive = this.executiveSubscriptionState.isFreeExecutive();

    if (isFreeExecutive) {
      return [
        {
          label: 'Principal',
          items: [
            {name: 'Tableau de bord', icon: 'dashboard', link: [...base, 'tableau-de-bord']},
            {name: 'Mon Wallet', icon: 'wallet', link: [...base, 'wallet']},
            {name: 'Mon entreprise', icon: 'apartment', link: [...base, 'entreprise']},
            {name: 'Dossier fiscal', icon: 'gavel', link: [...base, 'dossier-fiscal']},
            {name: 'Documents', icon: 'folder_open', link: [...base, 'documents']},
          ],
        },
        {
          label: 'Autres',
          items: [
            {name: 'Paramètres', icon: 'settings', link: [...base, 'parametres']},
          ],
        },
      ];
    }

    const principal: INavigationItem[] = [
      {name: 'Tableau de bord', icon: 'dashboard', link: [...base, 'tableau-de-bord']},
      {name: 'Mon Wallet', icon: 'wallet', link: [...base, 'wallet']},
      {name: 'Mon entreprise', icon: 'apartment', link: [...base, 'entreprise']},
      {name: 'KBIS', icon: 'description', link: [...base, 'kbis']},
      {name: 'RIB', icon: 'account_balance', link: [...base, 'rib']},
      {name: 'Certifications', icon: 'workspace_premium', link: [...base, 'certifications']},
      {name: 'Attestations', icon: 'verified', link: [...base, 'attestations']},
      {name: 'Dossier fiscal', icon: 'gavel', link: [...base, 'dossier-fiscal']},
      {name: 'Documents', icon: 'folder_open', link: [...base, 'documents']},
      {name: 'Transmettre', icon: 'send', link: [...base, 'transmettre']},
      {name: 'Veille juridique', icon: 'balance', link: [...base, 'veille-juridique']},
      {name: 'Surveillance', icon: 'visibility', link: [...base, 'surveillance']},
      {name: 'Mes alertes', icon: 'warning', link: [...base, 'alertes']},
    ];
    const autres: INavigationItem[] = [
      {name: 'Notifications', icon: 'notifications', link: [...base, 'notifications']},
      {name: 'Paramètres', icon: 'settings', link: [...base, 'parametres']},
    ];
    return [
      {label: 'Principal', items: principal},
      {label: 'Autres', items: autres},
    ];
  });
}
